"use client";

import { Button, Dropdown, Modal } from "flowbite-react";
import { BsThreeDotsVertical } from "react-icons/bs";
import {
  MdDeleteSweep,
  MdEdit,
  MdOutlineReply,
  MdAccessTime,
  MdCheckCircle,
  MdVisibility,
  MdEditNote,
  MdDeleteOutline,
  MdCheck,
} from "react-icons/md";
import { AiOutlineFilePdf, AiOutlineDownload } from "react-icons/ai";
import { BiCheckDouble } from "react-icons/bi";
import React, { useState, useRef, useEffect, useContext } from "react";
import { Message } from "@/models/Message";
import { SeenEntry } from "@/models/ChatMessage";
import { ChatContext, ChatContextInterface } from "../components/MSRContext";
import { FaCircleUser } from "react-icons/fa6";
import { FileMessage } from "../models/FileMessage";
import { RiErrorWarningLine } from "react-icons/ri";

/* =========================================================================
   TYPES
   ========================================================================= */
type AContEnvelope = {
  v: number;
  session_id: string;
  message_id: string;
  type: string; // e.g. "ui.render_menu" | "ui.render_input" | "ui.render_notice"
  flow_id?: string;
  reply_to?: string | null;
  payload: any;
  ts: number;
};

type UiRenderMenuPayload = {
  menu_id: string;
  title?: string;
  items?: { id: string; label: string; icon?: string }[];
  persistent?: boolean;
};

type UiRenderNoticePayload = {
  level?: "info" | "warn" | "error";
  message?: string;
};

function stripWeirdChars(raw: string) {
  return raw
    .replace(/^\uFEFF/, "")
    .replace(/[\u200B-\u200D\u2060]/g, "") // zero-width
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, ""); // control
}

function safeSliceToBraces(raw: string) {
  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");
  if (start !== -1 && end !== -1 && end > start) return raw.slice(start, end + 1);
  return raw;
}

function normalizePythonish(raw: string) {
  return raw
    .replace(/'/g, '"')
    .replace(/\bNone\b/g, "null")
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false");
}

// Reduce escapes
function reduceEscapesStep(t: string): string {
  let out = t;
  for (let i = 0; i < 3; i++) {
    const prev = out;
    out = out.replace(/\\\\/g, "\\");
    if (out === prev) break;
  }
  out = out.replace(/\\"/g, '"');
  out = out
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t")
    .replace(/\\b/g, "\b")
    .replace(/\\f/g, "\f");
  return out;
}

// Strict schema guard
function hasRequiredEnvelopeKeys(o: any): o is AContEnvelope {
  if (!o || typeof o !== "object") return false;
  if (typeof o.v !== "number") return false;
  if (typeof o.type !== "string") return false;
  if (typeof o.ts !== "number") return false;
  return true;
}

// Heuristic: looks like JSON embedded in a string
function looksLikeJSONStringifiedJSON(text: string): boolean {
  if (/^\s*"/.test(text) && /"\s*$/.test(text) && text.includes("{") && text.includes("}")) return true;
  if (text.includes('\\"{') || text.includes('\\\\"{') || /\\{/.test(text)) return true;
  if (/\bNone\b|\bTrue\b|\bFalse\b/.test(text)) return true;
  return false;
}

/**
 * STRICT: Only parse if it *starts with* `{` and has required keys.
 * Plain strings will return null.
 */
export function tryParseAcontFromText(input?: unknown): AContEnvelope | null {
  if (input == null) return null;

  // Already an object? Validate strictly.
  if (typeof input === "object") {
    const o = input as any;
    return hasRequiredEnvelopeKeys(o) ? (o as AContEnvelope) : null;
  }

  let text = String(input);
  text = stripWeirdChars(text).trim();

  // Hard gate: must start with '{'
  if (!/^\s*\{/.test(text)) return null;

  // Try direct parse
  let primary = safeSliceToBraces(text);
  try {
    const p = JSON.parse(primary);
    if (hasRequiredEnvelopeKeys(p)) return p;
  } catch {
    // continue to rescue passes
  }

  // Only attempt rescue passes if it actually looks stringified
  if (!looksLikeJSONStringifiedJSON(text)) return null;

  // Rescue 1: python-ish normalization
  try {
    const p = JSON.parse(normalizePythonish(primary));
    if (hasRequiredEnvelopeKeys(p)) return p;
  } catch { }

  // Rescue 2: reduce escapes iteratively
  try {
    let t = primary;
    for (let i = 0; i < 3; i++) {
      t = reduceEscapesStep(t);
      const p = JSON.parse(t);
      if (hasRequiredEnvelopeKeys(p)) return p;
    }
  } catch { }

  // Rescue 3: brutal backslash collapse
  try {
    const brutal = primary.replace(/\\+/g, "\\");
    const p = JSON.parse(brutal);
    if (hasRequiredEnvelopeKeys(p)) return p;
  } catch { }

  console.warn("ACont parse skipped or failed (strict). Raw:", input);
  return null;
}

/* =========================================================================
   UI RENDERERS FOR ACONT
   ========================================================================= */
function renderMenuBubbleReadOnly(env: AContEnvelope, isUser = false) {
  const payload = (env.payload || {}) as UiRenderMenuPayload;

  return (
    <div
      className={`p-4 rounded-2xl shadow-sm border transition-all duration-200 
        ${isUser
          ? "bg-gradient-to-br from-purple-500 to-purple-700 text-white border-purple-400/20 rounded-br-md"
          : "bg-gradient-to-br from-blue-400 to-blue-500 text-white border-blue-300/20 rounded-bl-md"
        }`}
    >
      {!!payload?.title && <div className="text-sm font-semibold mb-3">{payload.title}</div>}

      <div className="flex flex-col gap-2">
        {(payload?.items ?? []).map((it) => (
          <div
            key={it.id}
            className="w-full rounded-xl px-4 py-3 text-left flex items-center justify-between
                       bg-white/15 border border-white/20 select-none cursor-default"
          >
            <span className="font-medium flex items-center gap-2">{it.label}</span>
            <span className="text-white/70">›</span>
          </div>
        ))}
      </div>

      {payload?.persistent && <div className="mt-3 text-[11px] opacity-80">This menu is persistent</div>}
    </div>
  );
}

function renderInputHintBubbleReadOnly(env: AContEnvelope, isUser = false) {
  const p = (env.payload || {}) as {
    title?: string;
    label?: string;
    placeholder?: string;
    submit_label?: string;
    via_composer?: boolean;
  };

  return (
    <div
      className={`p-3 rounded-2xl shadow-sm border transition-all duration-200
        ${isUser
          ? "bg-gradient-to-br from-purple-500 to-purple-700 text-white border-white/20 rounded-br-md"
          : "bg-blue-50 text-blue-800 border border-blue-200 rounded-bl-md"
        }`}
    >
      {p.title && <div className="text-sm font-semibold mb-1">{p.title}</div>}
      <div className="text-xs opacity-80">
        {p.label ? `${p.label}: ` : ""}
        {p.placeholder || "Type and press Enter…"}
      </div>
      {p.submit_label && (
        <div className={`mt-2 text-[11px] ${isUser ? "text-white/80" : "text-blue-700/70"}`}>
          Submit: <span className="font-medium">{p.submit_label}</span>
        </div>
      )}
      <div className={`mt-2 text-[11px] ${isUser ? "text-white/70" : "text-blue-700/60"}`}>(Read-only)</div>
    </div>
  );
}

function renderNoticeBubbleReadOnly(env: AContEnvelope, isUser = false) {
  const p = (env.payload || {}) as UiRenderNoticePayload;
  const level = (p.level || "info").toLowerCase() as "info" | "warn" | "error";

  const color =
    level === "error"
      ? "from-red-500 to-red-600 border-white/30"
      : level === "warn"
        ? "from-yellow-500 to-yellow-600 border-white/30"
        : "from-blue-500 to-blue-600 border-white/30";

  return (
    <div
      className={`p-3 rounded-2xl shadow-sm border bg-gradient-to-br ${color} text-white
                  ${isUser ? "rounded-br-md" : "rounded-bl-md"}`}
    >
      <div className="text-sm">{p.message || "(no message)"}</div>
      <div className="mt-1 text-[11px] opacity-80">(Read-only)</div>
    </div>
  );
}

function renderAcontBubble(env: AContEnvelope, isUser = false) {
  const t = String(env.type || "").toLowerCase();

  switch (t) {
    case "ui.render_menu":
      return renderMenuBubbleReadOnly(env, isUser);

    case "ui.render_input":
      if (env.payload?.via_composer) {
        return renderInputHintBubbleReadOnly(env, isUser);
      }
      return renderNoticeBubbleReadOnly(
        { ...env, payload: { level: "info", message: env.payload?.title || "Input requested" } },
        isUser
      );

    case "ui.render_notice":
      return renderNoticeBubbleReadOnly(env, isUser);

    default:
      if (t.startsWith("ui.")) {
        return renderNoticeBubbleReadOnly(
          { ...env, payload: { level: "info", message: `(${t}) payload received` } },
          isUser
        );
      }
      return null;
  }
}

/* =========================================================================
   PROPS
   ========================================================================= */
interface MessageLayoutProps {
  message: Message | FileMessage | any;
  sender?: string;
  onEditMessage: (messageId: string, newText: string) => void;
  handleDeleteMessage: (messageId: string, chatroomId: string) => void;
  handleUndoMessage: (messageId: string, toName: string) => void;
  onReply: (message: Message) => void;
  onNavigateToMessage?: (messageId: string) => void;
  isHighlighted?: boolean;
  currentRoom: any
}

/* =========================================================================
   COMPONENT
   ========================================================================= */
const MessageLayout = ({
  message,
  sender,
  onEditMessage,
  handleDeleteMessage,
  handleUndoMessage,
  onReply,
  onNavigateToMessage,
  isHighlighted = false,
  currentRoom
}: MessageLayoutProps) => {
  if (!message) return null;

  const [isEditing, setIsEditing] = useState(false);
  const [editedText, setEditedText] = useState(message.text || "");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [unsendMessageWindow, setunsendMessageWindow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const MAX_MESSAGE_LENGTH = 200;
  const REPLIED_TO_MAX_LENGTH = 50;

  const messageText = message.text || "";

  // ACont detection (strict)
  const maybeAcont = tryParseAcontFromText(messageText);
  const isAcontUI = !!(maybeAcont && String(maybeAcont.type || "").toLowerCase().startsWith("ui."));
  const shouldTruncate = !isAcontUI && messageText.length > MAX_MESSAGE_LENGTH;

  const displayText =
    shouldTruncate && !isExpanded ? messageText.substring(0, MAX_MESSAGE_LENGTH) + "..." : messageText;

  const getRepliedText = () => {
    if (!message.repliedTo) return "";

    // Check if it's a file message - don't show any JSON or text content
    if (message.repliedTo.isFileMessage) {
      const fileName = message.repliedTo.fileName;
      if (fileName) {
        // Show just the filename, truncated if too long
        const maxFileNameLength = 30;
        if (fileName.length > maxFileNameLength) {
          return `📎 ${fileName.substring(0, maxFileNameLength)}...`;
        }
        return `📎 ${fileName}`;
      }
      return `📎 File`;
    }

    // Regular text message - ensure we don't show empty or JSON-like content
    const text = message.repliedTo.text || "";

    // Skip if text looks like JSON or is empty
    if (!text.trim() || text.trim().startsWith('{') || text.trim().startsWith('[')) {
      return "Message";
    }

    if (text.length > REPLIED_TO_MAX_LENGTH) {
      return text.substring(0, REPLIED_TO_MAX_LENGTH) + "...";
    }
    return text;
  };

  const repliedText = getRepliedText();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleRepliedMessageClick = () => {
    if (message.repliedTo?.messageId && onNavigateToMessage) {
      onNavigateToMessage(message.repliedTo.messageId);
    }
  };

  const formatTimestamp = (timestamp: string | number | Date | undefined) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString([], {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const renderStatusIcon = () => {
    if (!message.isOwner) return null;
    if (message.seenTimes && message.seenTimes.length > 0) {
      return <BiCheckDouble className="w-4 h-4 text-blue-500" />;
    } else if (message.deliveredTime) {
      return <BiCheckDouble className="w-4 h-4 text-gray-500" />;
    } else if (message.sentTime) {
      return <MdCheck className="w-4 h-4 text-gray-500" />;
    }
  };

  /* ===================== Markdown helpers ===================== */
  const parseList = (
    lines: string[],
    startIndex: number,
    isUser: boolean
  ): { element: JSX.Element; nextIndex: number } => {
    const items: { content: string; children?: JSX.Element; indent: number; type: "ul" | "ol" }[] =
      [];
    let i = startIndex;
    let listType: "ul" | "ol" = "ul";
    const firstLine = lines[i].trim();
    if (firstLine.match(/^\d+\.\s*/)) listType = "ol";
    const baseIndent = lines[i].search(/\S/);

    while (i < lines.length) {
      const line = lines[i];
      const indent = line.search(/\S/);
      const trimmedLine = line.trim();
      const ulMatch = trimmedLine.match(/^[\*\-\+]\s*(.*)/);
      const olMatch = trimmedLine.match(/^\d+\.\s*(.*)/);

      if (indent < baseIndent && (ulMatch || olMatch)) break;
      if (!ulMatch && !olMatch && indent <= baseIndent) break;

      if (ulMatch || olMatch) {
        const content = ulMatch ? ulMatch[1] : olMatch ? olMatch[1] : "";
        const currentType = ulMatch ? "ul" : "ol";
        let nestedList: JSX.Element | undefined;
        const tempIndex = i + 1;

        if (tempIndex < lines.length) {
          const nextLine = lines[tempIndex];
          const nextIndent = nextLine.search(/\S/);
          const nextTrimmed = nextLine.trim();

          if (
            nextIndent > indent &&
            (nextTrimmed.match(/^[\*\-\+]\s*/) || nextTrimmed.match(/^\d+\.\s*/))
          ) {
            const nestedResult = parseList(lines, tempIndex, isUser);
            nestedList = nestedResult.element;
            i = nestedResult.nextIndex - 1;
          }
        }

        items.push({ content, children: nestedList, indent, type: currentType });
      }

      i++;
    }

    const ListTag = listType === "ul" ? "ul" : "ol";
    const listClass = listType === "ul" ? "list-disc" : "list-decimal";

    const element = (
      <ListTag key={`list-${startIndex}`} className={`${listClass} pl-6 my-2 space-y-1`}>
        {items.map((item, idx) => (
          <li key={idx} className="text-sm">
            {item.content ? formatInlineMarkdown(item.content, isUser) : <span>&nbsp;</span>}
            {item.children && item.children}
          </li>
        ))}
      </ListTag>
    );

    return { element, nextIndex: i };
  };

  const formatInlineMarkdown = (text: string, isUser: boolean): (string | JSX.Element)[] => {
    if (!text) return [];
    const elements: (string | JSX.Element)[] = [];
    let remaining = text;
    let keyCounter = 0;

    while (remaining.length > 0) {
      let matched = false;

      const boldItalicMatch = remaining.match(/^\*\*\*([^*]+)\*\*\*/);
      if (boldItalicMatch) {
        elements.push(
          <strong key={`bi-${keyCounter++}`}>
            <em>{boldItalicMatch[1]}</em>
          </strong>
        );
        remaining = remaining.substring(boldItalicMatch[0].length);
        matched = true;
      }

      if (!matched) {
        const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/);
        if (boldMatch) {
          elements.push(<strong key={`b-${keyCounter++}`}>{boldMatch[1]}</strong>);
          remaining = remaining.substring(boldMatch[0].length);
          matched = true;
        }
      }

      if (!matched) {
        const italicMatch = remaining.match(/^\*([^*]+)\*/);
        if (italicMatch) {
          elements.push(<em key={`i-${keyCounter++}`}>{italicMatch[1]}</em>);
          remaining = remaining.substring(italicMatch[0].length);
          matched = true;
        }
      }

      if (!matched) {
        const codeMatch = remaining.match(/^`([^`]+)`/);
        if (codeMatch) {
          elements.push(
            <code
              key={`c-${keyCounter++}`}
              className={`px-1.5 py-0.5 rounded text-xs font-mono ${message.isOwner
                ? "bg-purple-300/30 text-white border border-purple-200/30"
                : "bg-gray-500 text-white border border-gray-300"
                }`}
            >
              {codeMatch[1]}
            </code>
          );
          remaining = remaining.substring(codeMatch[0].length);
          matched = true;
        }
      }

      if (!matched) {
        const strikeMatch = remaining.match(/^~~([^~]+)~~/);
        if (strikeMatch) {
          elements.push(
            <del key={`s-${keyCounter++}`} className="line-through">
              {strikeMatch[1]}
            </del>
          );
          remaining = remaining.substring(strikeMatch[0].length);
          matched = true;
        }
      }

      if (!matched) {
        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          elements.push(
            <a
              key={`a-${keyCounter++}`}
              href={linkMatch[2]}
              target="_blank"
              rel="noopener noreferrer"
              className={`underline hover:no-underline ${isUser
                ? "text-purple-200 hover:text-white"
                : "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                }`}
            >
              {linkMatch[1]}
            </a>
          );
          remaining = remaining.substring(linkMatch[0].length);
          matched = true;
        }
      }

      if (!matched) {
        const urlMatch = remaining.match(/^(https?:\/\/[^\s]+)/);
        if (urlMatch) {
          elements.push(
            <a
              key={`url-${keyCounter++}`}
              href={urlMatch[1]}
              target="_blank"
              className={`underline hover:no-underline break-all ${isUser
                ? "text-purple-200 hover:text-white"
                : "text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                }`}
            >
              {urlMatch[1]}
            </a>
          );
          remaining = remaining.substring(urlMatch[0].length);
          matched = true;
        }
      }

      if (!matched) {
        const nextSpecial = remaining.search(/[\*`~\[]/);
        const nextUrl = remaining.search(/https?:\/\//);
        let nextIndex = -1;
        if (nextSpecial !== -1 && nextUrl !== -1) nextIndex = Math.min(nextSpecial, nextUrl);
        else if (nextSpecial !== -1) nextIndex = nextSpecial;
        else if (nextUrl !== -1) nextIndex = nextUrl;

        if (nextIndex === -1) {
          elements.push(remaining);
          remaining = "";
        } else if (nextIndex === 0) {
          elements.push(remaining.charAt(0));
          remaining = remaining.substring(1);
        } else {
          elements.push(remaining.substring(0, nextIndex));
          remaining = remaining.substring(nextIndex);
        }
      }
    }

    return elements.length > 0 ? elements : [text];
  };

  const parseMarkdown = (text: string, isUser: boolean) => {
    if (!text) return null;

    const elements: JSX.Element[] = [];
    let key = 0;
    const lines = text.split("\n");
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        i++;
        continue;
      }

      if (trimmedLine.startsWith("```")) {
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith("```")) {
          codeLines.push(lines[i]);
          i++;
        }
        if (i < lines.length) i++;
        elements.push(
          <pre
            key={key++}
            className={`p-3 rounded-lg text-xs font-mono overflow-x-auto my-2 ${isUser
              ? "bg-purple-600 text-purple-100"
              : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
              }`}
          >
            <code>{codeLines.join("\n")}</code>
          </pre>
        );
        continue;
      }

      const headerMatch = trimmedLine.match(/^(#{1,6})\s+(.+)/);
      if (headerMatch) {
        const level = headerMatch[1].length;
        const headerText = headerMatch[2];
        const HeaderTag = `h${Math.min(level, 6)}` as keyof JSX.IntrinsicElements;

        const sizeClasses = {
          1: "text-xl font-bold mb-3 mt-4",
          2: "text-lg font-bold mb-2 mt-3",
          3: "text-base font-bold mb-2 mt-3",
          4: "text-sm font-bold mb-1 mt-2",
          5: "text-sm font-semibold mb-1 mt-2",
          6: "text-xs font-semibold mb-1 mt-2",
        };

        elements.push(
          <HeaderTag key={key++} className={sizeClasses[level as keyof typeof sizeClasses]}>
            {formatInlineMarkdown(headerText, isUser)}
          </HeaderTag>
        );
        i++;
        continue;
      }

      if (trimmedLine.startsWith("> ")) {
        const quoteLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith("> ")) {
          quoteLines.push(lines[i].trim().substring(2));
          i++;
        }

        elements.push(
          <blockquote
            key={key++}
            className={`border-l-4 pl-4 my-2 italic ${isUser
              ? "border-purple-300 text-purple-100"
              : "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300"
              }`}
          >
            {quoteLines.map((quoteLine, idx) => (
              <p key={idx} className="mb-1 last:mb-0">
                {formatInlineMarkdown(quoteLine, isUser)}
              </p>
            ))}
          </blockquote>
        );
        continue;
      }

      if (/^(\s*)[\*\-\+]\s+[^*]/.test(line) || /^(\s*)\d+\.\s+/.test(line)) {
        const listElement = parseList(lines, i, isUser);
        elements.push(listElement.element);
        i = listElement.nextIndex;
        key++;
        continue;
      }

      if (trimmedLine.match(/^[\*\-_]{3,}$/)) {
        elements.push(
          <hr
            key={key++}
            className={`my-3 ${isUser ? "border-purple-300" : "border-gray-300 dark:border-gray-600"}`}
          />
        );
        i++;
        continue;
      }

      if (trimmedLine.includes("|") && i + 1 < lines.length && lines[i + 1].includes("|")) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].includes("|")) {
          tableLines.push(lines[i]);
          i++;
        }

        if (tableLines.length >= 2) {
          const headers = tableLines[0]
            .split("|")
            .map((h) => h.trim())
            .filter((h) => h);
          const rows = tableLines.slice(2).map((row) =>
            row
              .split("|")
              .map((cell) => cell.trim())
              .filter((cell) => cell)
          );

          elements.push(
            <div key={key++} className="overflow-x-auto my-2">
              <table
                className={`min-w-full text-sm ${message.isOwner ? "border border-purple-100/30" : "border border-gray-400"
                  }`}
              >
                <thead className={message.isOwner ? "bg-purple-300/10" : "bg-gray-100"}>
                  <tr
                    className={`border-b ${message.isOwner ? "border-purple-400/30" : "border-gray-300"
                      }`}
                  >
                    {headers.map((header, idx) => (
                      <th
                        key={idx}
                        className={`px-2 py-1 text-left font-semibold ${message.isOwner ? "text-white" : "text-black"
                          }`}
                      >
                        {formatInlineMarkdown(header, isUser)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, rowIdx) => (
                    <tr
                      key={rowIdx}
                      className={`border-b ${message.isOwner ? "border-purple-400/20" : "border-gray-200"
                        }`}
                    >
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className={`px-2 py-1 ${message.isOwner ? "text-white" : "text-gray-800"
                            }`}
                        >
                          {formatInlineMarkdown(cell, isUser)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
          continue;
        }
      }

      const paragraphLines: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() &&
        !lines[i].trim().startsWith("#") &&
        !lines[i].trim().startsWith(">") &&
        !lines[i].trim().match(/^[\*\-\+]\s+/) &&
        !lines[i].trim().match(/^\d+\.\s+/) &&
        !lines[i].trim().startsWith("```") &&
        !lines[i].trim().match(/^[\*\-_]{3,}$/)
      ) {
        paragraphLines.push(lines[i]);
        i++;
      }

      if (paragraphLines.length > 0) {
        elements.push(
          <p key={key++} className="mb-2 last:mb-0 text-sm leading-relaxed">
            {formatInlineMarkdown(paragraphLines.join(" "), isUser)}
          </p>
        );
      }
    }

    return <div className="space-y-1">{elements}</div>;
  };

  const isUser = message.isOwner;
  const formattedTime = isUser
    ? message.sentTime
      ? new Date(message.sentTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : message.arrivalTime
        ? new Date(message.arrivalTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : ""
    : message.deliveredTime
      ? new Date(message.deliveredTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : message.arrivalTime
        ? new Date(message.arrivalTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
        : "";

  const getMessageBubbleWidth = (text: string | undefined, isUser: boolean) => {
    if (!text)
      return isUser ? "max-w-[280px] sm:max-w-[320px] md:max-w-md" : "max-w-[300px] sm:max-w-[340px] md:max-w-lg";
    const length = text.length;
    if (length <= 10) return "w-fit min-w-[120px]";
    else if (length <= 30)
      return isUser ? "w-fit max-w-[200px] sm:max-w-[240px]" : "w-fit max-w-[220px] sm:max-w-[260px]";
    else if (length <= 100)
      return isUser
        ? "w-fit max-w-[240px] sm:max-w-[280px] md:max-w-sm"
        : "w-fit max-w-[260px] sm:max-w-[300px] md:max-w-md";
    return isUser ? "max-w-[280px] sm:max-w-[320px] md:max-w-md" : "max-w-[300px] sm:max-w-[340px] md:max-w-lg";
  };


  const isFileMessage = message?.message_json?.job === "file-transfer" || false;

  const isDeleted = !!(
    (message as any).deletedTime ??
    (message as any).deleteTime ??
    (message as any)["message_deleted_at"]
  );

  const shouldHideFileContent = isFileMessage && isDeleted;
  const fileContent = message?.message_json?.parameters ? message?.message_json?.parameters : null;
  const fileData = (() => {
    if (isFileMessage && fileContent) {
      return { name: fileContent["file-name"], url: fileContent["file-location"], type: fileContent["file-type"] };
    }
    return null;
  })();

  const getFilePreview = () => {
    if (!fileData) return null;

    const openNewTab = (e: React.MouseEvent) => {
      e.stopPropagation();
      window.open(fileData.url, "_blank", "noopener,noreferrer");
    };
    if (shouldHideFileContent) {
      return (
        <div className="">
          Message was deleted
        </div>
      );
    }
    // IMAGE
    if (fileData.type?.startsWith("image/")) {
      return (
        <img
          src={fileData.url}
          alt={fileData.name}
          className="h-40 max-w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
          onClick={openNewTab}
        />
      );
    }

    // PDF
    if (fileData.type === "application/pdf") {
      return (
        <div className="flex items-center p-2 space-x-2 rounded-lg bg-purple-50 dark:bg-gray-600">
          <AiOutlineFilePdf className="text-2xl text-red-500" />
          <a
            href={fileData.url}
            target="_blank"
            rel="noopener noreferrer"
            download={fileData.name}
            className="underline"
            onClick={(e) => e.stopPropagation()}
          >
            {fileData.name}
          </a>
        </div>
      );
    }

    // ALL OTHER FILES 
    return (
      <div className="flex items-center p-2 space-x-2 bg-gray-200 rounded-lg dark:bg-gray-600">
        <AiOutlineDownload className="text-2xl text-blue-500" />
        <a
          href={fileData.url}
          target="_blank"
          rel="noopener noreferrer"
          download={fileData.name}
          className="underline"
          onClick={(e) => e.stopPropagation()}
        >
          {fileData.name}
        </a>
      </div>
    );
  };

  function getSenderUsername(message: any) {
    const { client_username, agent_username } = message?.sender || {};
    const username = client_username?.split("@")[0] || agent_username?.split("@")[0];
    return username || "";
  }


  /* ===================== JSX ===================== */
  return (
    <div
      className={`flex items-start gap-2 sm:gap-2.5 mx-1 sm:mx-2 mb-2 sm:mb-3 ${isUser ? "flex-row-reverse" : ""
        }`}
    >
      {/* Avatar */}
      {!isUser && (
        <div className="flex-shrink-0">
          <FaCircleUser className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600 rounded-full" />
        </div>
      )}

      <div
        className={`flex flex-col mb-5 leading-1.5 p-3 sm:p-4 transition-all duration-500 ease-in-out
          ${isDeleted
            ? isUser
              ? "bg-purple-200 text-purple-800 opacity-70 rounded-s-xl rounded-ee-xl border-2 border-dashed border-purple-400"
              : "bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 opacity-70 rounded-e-xl rounded-es-xl border-2 border-dashed border-gray-400 dark:border-gray-500"
            : isUser
              ? "bg-purple-300 text-purple-950 rounded-s-xl rounded-ee-xl"
              : "bg-purple-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-e-xl rounded-es-xl"
          }
          ${getMessageBubbleWidth(messageText, isUser)}
          border-gray-200 shadow-sm
          ${isHighlighted
            ? ` ring-opacity-60 shadow-lg scale-[1.02] transform
               ${isUser
              ? "bg-purple-400 shadow-purple-200 ring-4 ring-purple-300"
              : "bg-gray-50 dark:bg-gray-900/30 shadow-gray-200 dark:shadow-gray-800 ring-4 ring-gray-400"
            }`
            : "hover:shadow-md hover:-translate-y-0.5"
          }`}
      >
        {/* Sender (non-owner) */}
        {!isUser && (
          <div className="flex items-center justify-between mb-1">
            <span
              className={`text-xs sm:text-sm font-semibold truncate transition-colors duration-500 ${
                isHighlighted ? "text-yellow-800 dark:text-yellow-200 animate-pulse" : ""
              }`}
            >
              {getSenderUsername(message)}
            </span>
          </div>
        )}

        {/* Replied preview */}
        {message.repliedTo && message.repliedTo.messageId && (
          <div
            className={`p-2 sm:p-3 border-l-4 rounded-md mb-2 cursor-pointer transition-all duration-300 hover:shadow-md transform hover:scale-[1.01] ${isUser
              ? "bg-purple-600 border-purple-300 text-purple-100 hover:bg-purple-500"
              : "bg-gray-50 border-gray-300 text-gray-600 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"
              } ${isHighlighted
                ? "border-yellow-400 bg-yellow-100 dark:bg-yellow-800/30 text-yellow-800 dark:text-yellow-200"
                : ""
              }`}
            onClick={handleRepliedMessageClick}
            title="Click to go to original message"
          >
            <div className="flex items-center gap-1 mb-1">
              <MdOutlineReply
                className={`w-3 h-3 opacity-70 transition-all duration-500 ${isHighlighted ? "animate-pulse text-yellow-600 dark:text-yellow-300 scale-110" : ""
                  }`}
              />
              <span className="text-xs font-medium opacity-70">
                {message.sender?.agent_username
                  ? message.sender.agent_username.split("@")[0]
                  : "Unknown dsadsa"}
              </span>
            </div>
            <p className="text-xs line-clamp-2">{repliedText}</p>
          </div>
        )}

        {/* Content */}
        {isEditing ? (
          <div className="w-full">
            <textarea
              className="w-full p-2 text-sm text-black border border-purple-400 rounded bg-purple-50 resize-none dark:bg-gray-600 dark:text-white min-h-[60px] focus:outline-none focus:ring-2 focus:ring-purple-300"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              rows={3}
            />
            <div className="flex justify-end mt-2 space-x-2">
              <Button
                size="xs"
                color="primary"
                onClick={() => {
                  console.log("ON EDIT MESSAGE", message)
                  onEditMessage(message.messageId, editedText);
                  setIsEditing(false);
                }}
              >
                Save
              </Button>
              <Button
                size="xs"
                color="gray"
                onClick={() => {
                  setIsEditing(false);
                  setEditedText(message.text || "");
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <div className="w-full">
            {/* {isDeleted && (
              <div className="flex items-center gap-2 mb-2 text-xs italic text-red-600 dark:text-red-400">
                <MdDeleteOutline className="w-4 h-4" />
                <span>This message was deleted</span>
              </div>
            )} */}
            {messageText && !isFileMessage && (
              <div className="relative">
                <div className={`text-sm font-normal py-1 sm:py-2.5 break-words leading-relaxed ${isDeleted ? 'line-through opacity-60' : ''}`}>
                  {isAcontUI ? renderAcontBubble(maybeAcont!, isUser) : parseMarkdown(displayText, isUser)}
                </div>

                {!isAcontUI && shouldTruncate && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`text-xs font-medium mt-1 hover:underline transition-all duration-300 hover:scale-105
                      ${isUser
                        ? "text-purple-200 hover:text-white"
                        : "text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300"
                      }
                      ${isHighlighted && !isUser ? "text-yellow-700 dark:text-yellow-300" : ""}`}
                  >
                    {isExpanded ? "Read less" : "Read more"}
                  </button>
                )}
              </div>
            )}

            {/* File Preview */}
            {isFileMessage && <div className="mt-2 w-full overflow-hidden">{getFilePreview()}</div>}

            {/* Time + status */}
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center gap-1">
                <span
                  className={`text-xs sm:text-sm text-gray-600 dark:text-gray-300 transition-all duration-500 ${isHighlighted && !isUser ? "text-yellow-700 dark:text-yellow-400 font-medium" : ""
                    }`}
                >
                  {formattedTime}
                </span>
                {message.editedTime && !isDeleted && (
                  <span className="text-[10px] italic text-gray-500 dark:text-gray-400 ml-1">
                    edited
                  </span>
                )}
              </div>
              <div className="flex items-center ml-2">{renderStatusIcon()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Dropdown */}
      <div className="flex-shrink-0 self-center mt-1" ref={dropdownRef}>
        <Dropdown
          color="gray"
          inline
          label={
            <BsThreeDotsVertical
              className={`w-4 h-4 transition-colors duration-300 ${isUser ? "text-purple-950 dark:text-white " : "text-gray-900 dark:text-gray-400"
                } ${isHighlighted ? "text-yellow-600 dark:text-yellow-300" : ""}`}
            />
          }
          theme={{ arrowIcon: "hidden" }}
          className="bg-white rounded-lg dark:bg-gray-900 shadow-md"
          placement={isUser ? "left-start" : "right-start"}
        >
          {isUser && !isFileMessage && !isDeleted && (
            <Dropdown.Item
              className="flex items-center gap-x-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600"
              onClick={() => {
                setIsEditing(true);
                setIsDropdownOpen(false);
              }}
            >
              <MdEdit className="w-4 h-4" />
              <span>Edit</span>
            </Dropdown.Item>
          )}

          {message.isOwner && !isDeleted && (
            <Dropdown.Item
              className="flex items-center gap-x-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600"
              onClick={() => setunsendMessageWindow(true)}
            >
              <MdDeleteSweep className="w-4 h-4" />
              <span>{isDeleted ? "Deleted" : "Unsend"}</span>
            </Dropdown.Item>
          )}
          
          {!isDeleted && (
            <Dropdown.Item
              className="flex items-center gap-x-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600"
              onClick={() => onReply(message as Message)}
            >
              <MdOutlineReply className="w-4 h-4" />
              <span>Reply</span>
            </Dropdown.Item>
          )}
          <Dropdown.Item
            className="flex items-center gap-x-2 px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-600"
            onClick={() => setShowDetails(true)}
          >
            <RiErrorWarningLine className="w-4 h-4" />
            <span>Details</span>
          </Dropdown.Item>
        </Dropdown>
      </div>

      {/* Image Modal */}
      {fileData && fileData.type.startsWith("image/") && (
        <Modal
          onClose={() => setShowImageModal(false)}
          popup
          show={showImageModal}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          <Modal.Body className="relative p-0 bg-purple-100 rounded-lg shadow-xl ">
            <div className="absolute top-4 right-4 z-10 flex space-x-2">
              <a
                href={fileData.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
                title="Download"
              >
                <AiOutlineDownload className="w-6 h-6" />
              </a>

              <button
                onClick={() => setShowImageModal(false)}
                className="inline-flex items-center rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
                title="Close"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="flex items-center justify-center w-full h-full min-h-[300px]">
              <img
                src={fileData.url}
                alt={fileData.name}
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            </div>

            <div className="absolute bottom-4 left-4 right-4 text-center">
              <p className="text-white text-sm bg-black/50 rounded px-3 py-2 inline-block">
                {fileData.name}
              </p>
            </div>
          </Modal.Body>
        </Modal>
      )}

      {/* Message Details Modal */}
      <Modal
        onClose={() => setShowDetails(false)}
        popup
        size="md"
        show={showDetails}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* {console.log("inside modal", message)} */}
        <Modal.Body className="relative p-0 bg-white rounded-lg shadow-xl dark:bg-gray-8 00 w-full max-w-md">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-purple-500 to-purple-600 rounded-t-lg">
            <h3 className="text-lg font-semibold text-white">Message Details</h3>
            <button
              onClick={() => setShowDetails(false)}
              className="inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-white hover:bg-white/20 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div className="p-6 space-y-4">
            {message.sentTime &&

              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                    <MdAccessTime className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Sent</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTimestamp(message.sentTime || undefined)}
                  </p>
                </div>
              </div>
            }
            {message.deliveredTime &&
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-green-100 dark:bg-green-900 rounded-lg flex items-center justify-center">
                    <MdCheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Delivered</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTimestamp(message.deliveredTime || undefined)}
                  </p>
                </div>
              </div>
            }
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center">
                  <MdVisibility className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">Read By</p>
                {message.seenTimes && message.seenTimes.length > 0 ? (
                  <div className="space-y-1 mt-1">
                    {message.seenTimes.map((seenEntry: SeenEntry, index: number) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-xs text-gray-600 dark:text-gray-300 font-medium">
                          {seenEntry.name ? seenEntry.name.split("@")[0] : "Unknown User"}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTimestamp(seenEntry.time)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Not seen yet</p>
                )}
              </div>
            </div>

            {message.editedTime && (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-yellow-100 dark:bg-yellow-900 rounded-lg flex items-center justify-center">
                    <MdEditNote className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Last Edited</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTimestamp(message.editedTime)}
                  </p>
                </div>
              </div>
            )}

            {message.deletedTime && (
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 bg-red-100 dark:bg-red-900 rounded-lg flex items-center justify-center">
                    <MdDeleteOutline className="w-4 h-4 text-red-600 dark:text-red-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Deleted</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {formatTimestamp(message.deletedTime)}
                  </p>
                </div>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0">
                  <FaCircleUser className="w-8 h-8 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">Sender</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {message.sender?.agent_username ? message.sender.agent_username.split("@")[0] : "Unknown User"}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Message Preview</p>
              <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-3">
                  {message.text || "No text content"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
            <Button onClick={() => setShowDetails(false)} className="bg-purple-600 hover:bg-purple-700 text-white">
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>

      {/* Unsend Confirmation */}
      <Modal
        onClose={() => setunsendMessageWindow(false)}
        popup
        size="sm"
        show={unsendMessageWindow}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        <Modal.Body className="relative p-4 sm:p-6 text-center bg-white rounded-lg shadow-lg dark:bg-gray-800 w-full max-w-sm">
          <button
            onClick={() => setunsendMessageWindow(false)}
            className="absolute right-2.5 top-2.5 inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>

          <div className="mb-6">
            <p className="text-sm sm:text-base text-gray-500 dark:text-gray-300">
              Are you sure you want to delete this message?
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <Button color="cancel" onClick={() => setunsendMessageWindow(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            {currentRoom && (
              <Button
                color="primary"
                onClick={() => {
                  const agentUsername = currentRoom?.session_type === "c2a"
                    ? currentRoom?.client?.client_username
                    : currentRoom?.agent?.agent_username;
                  handleUndoMessage(message.messageId, agentUsername);
                  setunsendMessageWindow(false);
                }}
                className="w-full sm:w-auto"
              >
                Delete
              </Button>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default MessageLayout;